/**
 * ExternTidTab — project-connected version of ExternTid.
 * Used as a tab inside ProjectDetail.
 * The project manager uploads a Milltime export → stored in Firestore.
 * Each team member loads the tab, sees their own entries, marks days as done.
 * Progress is persisted per user in Firestore.
 */
import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Box, Typography, Paper, Chip, Select, MenuItem,
  FormControl, InputLabel, IconButton, Stack, Divider,
  Alert, LinearProgress, Tooltip, Button, CircularProgress,
  TextField, Collapse, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import UploadFileIcon           from '@mui/icons-material/UploadFile';
import ChevronLeftIcon          from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon         from '@mui/icons-material/ChevronRight';
import CheckCircleIcon          from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon          from '@mui/icons-material/ContentCopy';
import CheckIcon                from '@mui/icons-material/Check';
import CloudUploadIcon          from '@mui/icons-material/CloudUpload';
import EditIcon                 from '@mui/icons-material/Edit';
import DeleteOutlineIcon        from '@mui/icons-material/DeleteOutline';
import PushPinIcon              from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon      from '@mui/icons-material/PushPinOutlined';
import SaveIcon                 from '@mui/icons-material/Save';
import OpenInNewIcon            from '@mui/icons-material/OpenInNew';
import VisibilityIcon           from '@mui/icons-material/Visibility';
import VisibilityOffIcon        from '@mui/icons-material/VisibilityOff';
import SettingsIcon             from '@mui/icons-material/Settings';

import {
  uploadExternTidReport,
  deleteExternTidReport,
  subscribeExternTid,
  saveExternTidProgress,
  getExternTidProgress,
  saveExternTidSysConfig,
  subscribeExternTidSysConfig,
  saveExternTidCreds,
  getExternTidCreds,
} from '../../services/externtidService';

// Shared system config — visible to everyone in the project
const EMPTY_CONFIG = { systemnamn: '', url: '', server: '', databas: '', instruktioner: '' };
// Personal credentials — private per user
const EMPTY_CREDS  = { anvandarnamn: '', losenord: '' };

// ─── Parsing (same as ExternTid.jsx) ─────────────────────────────────────────

function splitCSVLine(line) {
  const parts = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ';' && !inQ) { parts.push(cur); cur = ''; }
    else { cur += ch; }
  }
  parts.push(cur);
  return parts;
}

function rowsToEntries(rows) {
  const entries = [];
  for (let i = 1; i < rows.length; i++) {
    const p = rows[i];
    if (!p || p.length < 9) continue;
    const enhet = String(p[8] ?? '').trim();
    if (enhet !== 'tim') continue;
    const antalRaw = p[7];
    const antal = typeof antalRaw === 'number'
      ? antalRaw
      : parseFloat(String(antalRaw ?? '').trim().replace(',', '.'));
    if (isNaN(antal) || antal <= 0) continue;
    let datum = p[2];
    if (datum instanceof Date) datum = isoDate(datum);
    else if (typeof datum === 'number') {
      const info = XLSX.SSF.parse_date_code(datum);
      datum = `${info.y}-${String(info.m).padStart(2,'0')}-${String(info.d).padStart(2,'0')}`;
    } else datum = String(datum ?? '').trim();
    if (!datum.match(/^\d{4}-\d{2}-\d{2}$/)) continue;
    entries.push({
      datum, antal,
      namn:      String(p[3] ?? '').trim(),
      aktivitet: String(p[4] ?? '').trim(),
      notering:  String(p[5] ?? '').trim(),
    });
  }
  return entries;
}

function parseCSVText(text) {
  const lines = text.replace(/\r\n/g,'\n').replace(/\r/g,'\n').split('\n');
  return rowsToEntries(lines.filter(l=>l.trim()).map(l=>splitCSVLine(l)));
}

function parseExcelBuffer(buf) {
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return rowsToEntries(XLSX.utils.sheet_to_json(ws, { header:1, defval:'' }));
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function mondayOf(date) {
  const d = new Date(date); d.setHours(0,0,0,0);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow===0 ? -6 : 1-dow));
  return d;
}
function weekNumber(date) {
  const d = new Date(date); d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - ((d.getDay()+6)%7));
  const jan4 = new Date(d.getFullYear(),0,4);
  return 1 + Math.round(((d-jan4)/86400000 - 3 + ((jan4.getDay()+6)%7))/7);
}

const SV_DAYS   = ['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];
const SV_MONTHS = ['Januari','Februari','Mars','April','Maj','Juni',
                   'Juli','Augusti','September','Oktober','November','December'];

function fmtH(h)     { return h%1===0 ? `${h}` : h.toFixed(1); }
function fmtHCopy(h) { return fmtH(h).replace('.', ','); }

function MonthCalendar({ month, person, byDate, copied, onDayClick, accentColor }) {
  const theme = useTheme();
  if (!month) return null;
  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow    = new Date(y, m-1, 1).getDay();
  const offset      = firstDow === 0 ? 6 : firstDow - 1;
  const cells = [...Array(offset).fill(null),
                 ...Array.from({length:daysInMonth}, (_,i)=>i+1)];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Box>
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', mb:0.5 }}>
        {['M','T','O','T','F','L','S'].map((d,i) => (
          <Typography key={i} variant="caption"
            sx={{ textAlign:'center', color:'text.disabled', fontSize:'0.62rem', fontWeight:600 }}>
            {d}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'3px' }}>
        {cells.map((day, i) => {
          if (!day) return <Box key={i} sx={{ aspectRatio:'1' }} />;
          const dateStr  = `${month}-${String(day).padStart(2,'0')}`;
          const dayH     = (byDate[dateStr]||[]).reduce((s,r)=>s+r.antal, 0);
          const isCopied = !!copied[`${person}:${dateStr}`];
          const hasH     = dayH > 0;
          const isWknd   = i%7 >= 5;
          return (
            <Tooltip key={i} title={hasH ? `${dateStr}: ${fmtH(dayH)}h${isCopied?' ✓':''}` : ''} arrow>
              <Box onClick={() => hasH && onDayClick(new Date(y,m-1,day))}
                sx={{
                  aspectRatio:'1', borderRadius:'4px',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  cursor: hasH ? 'pointer' : 'default',
                  bgcolor: isCopied ? alpha(theme.palette.success.main, 0.22)
                    : hasH ? alpha(accentColor, 0.15)
                    : isWknd ? alpha(theme.palette.text.primary, 0.03) : 'transparent',
                  border: hasH ? '1.5px solid' : '1px solid',
                  borderColor: isCopied ? theme.palette.success.main
                    : hasH ? accentColor : 'transparent',
                  '&:hover': hasH ? { bgcolor: isCopied
                    ? alpha(theme.palette.success.main, 0.35) : alpha(accentColor, 0.3) } : {},
                }}>
                <Typography sx={{ fontSize:'0.65rem', fontWeight:hasH?700:400, lineHeight:1,
                  color: isCopied ? theme.palette.success.main : hasH ? accentColor : 'text.disabled' }}>
                  {day}
                </Typography>
                {hasH && (
                  <Typography sx={{ fontSize:'0.55rem', lineHeight:1, mt:'1px',
                    color: isCopied?'success.main':accentColor }}>
                    {fmtH(dayH)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ExternTidTab({ projectId, currentUser, userProfile }) {
  const theme = useTheme();

  // Report loaded from Firestore
  const [report,    setReport]    = useState(undefined);
  const [uploading,  setUploading]  = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  // Pin state (localStorage, keyed by projectId)
  const isPinned = projectId
    ? (() => { try { return JSON.parse(localStorage.getItem('externtid_pins')||'[]').includes(projectId); } catch { return false; } })()
    : false;
  const [pinned, setPinned] = useState(isPinned);
  const togglePin = () => {
    try {
      const pins = JSON.parse(localStorage.getItem('externtid_pins') || '[]');
      const next = pins.includes(projectId)
        ? pins.filter(id => id !== projectId)
        : [...pins, projectId];
      localStorage.setItem('externtid_pins', JSON.stringify(next));
      setPinned(!pins.includes(projectId));
    } catch {}
  };

  // System config
  const [sysConfig,      setSysConfig]      = useState(null);
  const [editingConfig,  setEditingConfig]  = useState(false);
  const [configForm,     setConfigForm]     = useState(EMPTY_CONFIG);
  const [savingConfig,   setSavingConfig]   = useState(false);
  const [showPassword,   setShowPassword]   = useState(false);
  const [showConfigPanel, setShowConfigPanel] = useState(true);

  // Personal credentials (only current user's own)
  const [creds,         setCreds]         = useState(EMPTY_CREDS);
  const [editingCreds,  setEditingCreds]  = useState(false);
  const [credForm,      setCredForm]      = useState(EMPTY_CREDS);
  const [savingCreds,   setSavingCreds]   = useState(false);

  // View state
  const [person,     setPerson]     = useState('');
  const [month,      setMonth]      = useState('');
  const [weekStart,  setWeekStart]  = useState(null);
  const [lastCopied, setLastCopied] = useState(null);
  const [copied,     setCopied]     = useState({});
  const [loadingProg, setLoadingProg] = useState(false);

  const uid        = currentUser?.uid;
  const userName   = userProfile?.name || currentUser?.displayName || '';

  // ── Load report from Firestore ─────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    const unsub = subscribeExternTid(projectId, (data) => {
      setReport(data ?? null);
    });
    return unsub;
  }, [projectId]);

  // ── Load system config from Firestore ──────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    const unsub = subscribeExternTidSysConfig(projectId, (data) => {
      setSysConfig(data);
      if (data) setConfigForm({ ...EMPTY_CONFIG, ...data });
    });
    return unsub;
  }, [projectId]);

  // ── Load user's progress + credentials from Firestore ─────────────────────
  useEffect(() => {
    if (!projectId || !uid) return;
    setLoadingProg(true);
    Promise.all([
      getExternTidProgress(projectId, uid),
      getExternTidCreds(projectId, uid),
    ]).then(([days, savedCreds]) => {
      setCopied(days);
      setCreds(savedCreds);
      setCredForm({ ...EMPTY_CREDS, ...savedCreds });
      setLoadingProg(false);
    });
  }, [projectId, uid]);

  // ── When report loads, auto-select person + month ─────────────────────────
  useEffect(() => {
    if (!report?.entries) return;
    const entries = report.entries;
    const persons = [...new Set(entries.map(r => r.namn))].sort();
    const months  = [...new Set(entries.map(r => r.datum.slice(0,7)))].sort();

    // Auto-select current user if their name appears in the data
    const myName = persons.find(p =>
      userName && p.toLowerCase().includes(userName.split(' ')[0].toLowerCase())
    ) || persons[0] || '';
    const lastM = months[months.length-1] || '';

    setPerson(myName);
    setMonth(lastM);
    if (lastM) setWeekStart(mondayOf(new Date(lastM+'-01')));
  }, [report, userName]);

  // ── Save system config ─────────────────────────────────────────────────────
  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await saveExternTidSysConfig(projectId, configForm);
      setEditingConfig(false);
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Save personal credentials ──────────────────────────────────────────────
  const handleSaveCreds = async () => {
    setSavingCreds(true);
    try {
      await saveExternTidCreds(projectId, uid, credForm);
      setCreds(credForm);
      setEditingCreds(false);
    } finally {
      setSavingCreds(false);
    }
  };

  // ── Delete report ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteExternTidReport(projectId);
      setConfirmDel(false);
    } finally {
      setDeleting(false);
    }
  };

  // ── Upload handler ─────────────────────────────────────────────────────────
  const handleUploadFile = useCallback((file) => {
    if (!file || !projectId || !uid) return;
    setUploading(true);
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);

    const finalize = (entries) => {
      uploadExternTidReport(projectId, uid, userName, file.name, entries)
        .catch(err => console.error('ExternTid upload error', err))
        .finally(() => setUploading(false));
    };

    if (isExcel) {
      const r = new FileReader();
      r.onload = e => finalize(parseExcelBuffer(new Uint8Array(e.target.result)));
      r.readAsArrayBuffer(file);
    } else {
      const r = new FileReader();
      r.onload = e => {
        const text = e.target.result;
        if (text.includes('?')) {
          const r2 = new FileReader();
          r2.onload = e2 => finalize(parseCSVText(e2.target.result));
          r2.readAsText(file, 'windows-1252');
        } else {
          finalize(parseCSVText(text));
        }
      };
      r.readAsText(file, 'UTF-8');
    }
  }, [projectId, uid, userName]);

  // ── Save progress to Firestore ─────────────────────────────────────────────
  const persistCopied = useCallback((next) => {
    setCopied(next);
    if (projectId && uid) {
      saveExternTidProgress(projectId, uid, next)
        .catch(err => console.error('ExternTid progress save error', err));
    }
  }, [projectId, uid]);

  const toggleDay = (dateStr) => {
    const key = `${person}:${dateStr}`;
    persistCopied({ ...copied, [key]: !copied[key] });
  };

  const copyText = useCallback((text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setLastCopied(key);
      setTimeout(() => setLastCopied(c => c===key ? null : c), 1800);
    });
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────────
  const entries  = report?.entries || [];
  const persons  = [...new Set(entries.map(r=>r.namn))].sort();
  const months   = [...new Set(entries.map(r=>r.datum.slice(0,7)))].sort();

  const filtered = entries.filter(r =>
    (!person || r.namn === person) && (!month || r.datum.startsWith(month))
  );
  const byDate = {};
  filtered.forEach(r => { (byDate[r.datum] = byDate[r.datum]||[]).push(r); });

  const totalH = filtered.reduce((s,r)=>s+r.antal, 0);

  const weekDays = weekStart
    ? Array.from({length:7}, (_,i) => {
        const d = new Date(weekStart); d.setDate(d.getDate()+i); return d;
      })
    : [];

  const daysWithEntries  = weekDays.filter(d=>(byDate[isoDate(d)]||[]).length>0);
  const copiedCount      = daysWithEntries.filter(d=>copied[`${person}:${isoDate(d)}`]).length;
  const allCopied        = daysWithEntries.length>0 && copiedCount===daysWithEntries.length;
  const weekFullyMarked  = weekDays.length>0 && weekDays.every(d=>copied[`${person}:${isoDate(d)}`]);

  const markWeekDone = () => {
    const u = {...copied};
    weekDays.forEach(d => { u[`${person}:${isoDate(d)}`] = true; });
    persistCopied(u);
  };
  const unmarkWeek = () => {
    const u = {...copied};
    weekDays.forEach(d => { delete u[`${person}:${isoDate(d)}`]; });
    persistCopied(u);
  };

  const stepWeek = (delta) => {
    if (!weekStart) return;
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
    const thu = new Date(d); thu.setDate(thu.getDate() + 3);
    const m = `${thu.getFullYear()}-${String(thu.getMonth()+1).padStart(2,'0')}`;
    if (m !== month) setMonth(m);
  };
  const prevWeek = () => stepWeek(-1);
  const nextWeek = () => stepWeek(1);
  const handleMonthChange = (m) => {
    setMonth(m);
    if (m) setWeekStart(mondayOf(new Date(m+'-01')));
  };

  const monthLabel = month
    ? (() => { const [y,m] = month.split('-'); return `${SV_MONTHS[+m-1]} ${y}`; })()
    : '';
  const wkNum = weekStart ? weekNumber(weekStart) : '';

  const isDark      = theme.palette.mode === 'dark';
  const accentColor = theme.palette.secondary.main;
  const paperBg     = theme.palette.background.paper;
  const successBg   = alpha(theme.palette.success.main, 0.12);

  // ── Render: loading ────────────────────────────────────────────────────────
  if (report === undefined || loadingProg) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  // ── Render: upload zone (no data yet) ─────────────────────────────────────
  const uploadZone = (
    <Paper
      variant="outlined"
      component="label"
      htmlFor="et-tab-file"
      sx={{
        display:'block', p:4, textAlign:'center', cursor:'pointer',
        borderStyle:'dashed', borderColor:'divider', backgroundColor: paperBg,
        borderRadius:2, transition:'border-color 0.2s',
        '&:hover': { borderColor:'secondary.main' },
      }}
    >
      <CloudUploadIcon sx={{ fontSize:48, color:'text.disabled', mb:1 }} />
      <Typography variant="h6" sx={{ color:'text.secondary' }} gutterBottom>
        {report ? 'Ladda upp ny rapport' : 'Ladda upp tidrapport'}
      </Typography>
      <Typography variant="body2" sx={{ color:'text.disabled' }}>
        CSV eller Excel från Milltime (.csv, .xlsx, .xls)
      </Typography>
      <input
        id="et-tab-file" type="file" accept=".csv,.xlsx,.xls,.txt"
        style={{ display:'none' }}
        onChange={e => handleUploadFile(e.target.files[0])}
      />
    </Paper>
  );

  // ── System config panel (shared across both states) ───────────────────────
  const configPanel = (
    <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', backgroundColor: paperBg }}>
      {/* Header row */}
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 2, py: 1.5, cursor: 'pointer' }}
        onClick={() => { setShowConfigPanel(v => !v); setEditingConfig(false); }}
      >
        <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          {sysConfig?.systemnamn || 'Externt tidssystem'}
        </Typography>
        <Tooltip title={pinned ? 'Lossa från ExternTid-startsidan' : 'Fäst på ExternTid-startsidan'}>
          <IconButton size="small"
            onClick={e => { e.stopPropagation(); togglePin(); }}
            sx={{ color: pinned ? 'secondary.main' : 'text.disabled',
                  '&:hover': { color: 'secondary.main' } }}>
            {pinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        {sysConfig?.url && !showConfigPanel && (
          <Chip
            size="small" label={sysConfig.systemnamn || 'Öppna'} variant="outlined"
            icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
            component="a" href={sysConfig.url} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          />
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {showConfigPanel ? '▲' : '▼'}
        </Typography>
      </Stack>

      <Collapse in={showConfigPanel}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {editingConfig ? (
            // ── Edit form ───────────────────────────────────────────────────
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Systemnamn" fullWidth
                  value={configForm.systemnamn}
                  onChange={e => setConfigForm(f => ({ ...f, systemnamn: e.target.value }))}
                  placeholder="t.ex. Visma PX"
                />
                <TextField size="small" label="URL" fullWidth
                  value={configForm.url}
                  onChange={e => setConfigForm(f => ({ ...f, url: e.target.value }))}
                  placeholder="https://px.forsen.com/login/login.asp"
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Server" fullWidth
                  value={configForm.server}
                  onChange={e => setConfigForm(f => ({ ...f, server: e.target.value }))}
                />
                <TextField size="small" label="Databas" fullWidth
                  value={configForm.databas}
                  onChange={e => setConfigForm(f => ({ ...f, databas: e.target.value }))}
                />
              </Stack>
              <TextField size="small" label="Instruktioner" fullWidth multiline rows={3}
                value={configForm.instruktioner}
                onChange={e => setConfigForm(f => ({ ...f, instruktioner: e.target.value }))}
                placeholder="Beskriv hur man loggar in och rapporterar tid..."
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={() => { setEditingConfig(false); setConfigForm({ ...EMPTY_CONFIG, ...sysConfig }); }}>
                  Avbryt
                </Button>
                <Button size="small" variant="contained" startIcon={<SaveIcon />}
                  onClick={handleSaveConfig} disabled={savingConfig}>
                  {savingConfig ? 'Sparar…' : 'Spara'}
                </Button>
              </Stack>
            </Stack>
          ) : (
            // ── Display mode ─────────────────────────────────────────────────
            <Stack spacing={1.5}>
              {sysConfig ? (
                <>
                  {/* Fields row */}
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    {[
                      { label: 'URL', value: sysConfig.url, isLink: true },
                      { label: 'Server', value: sysConfig.server, copy: true },
                      { label: 'Databas', value: sysConfig.databas, copy: true },
                    ].filter(f => f.value).map(field => (
                      <Box key={field.label}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {field.label}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {field.isLink ? (
                            <Typography
                              component="a" href={field.value} target="_blank" rel="noopener noreferrer"
                              variant="body2"
                              sx={{ color: accentColor, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {field.value}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                              {field.secret ? '••••••••' : field.value}
                            </Typography>
                          )}
                          {field.copy && (
                            <Tooltip title={lastCopied === field.label ? 'Kopierat!' : `Kopiera ${field.label.toLowerCase()}`}>
                              <IconButton size="small"
                                onClick={() => copyText(field.value, field.label)}
                                sx={{ color: lastCopied === field.label ? 'success.main' : 'text.disabled' }}
                              >
                                {lastCopied === field.label
                                  ? <CheckIcon sx={{ fontSize: 14 }} />
                                  : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                  {sysConfig.instruktioner && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', mt: 0.5 }}>
                      {sysConfig.instruktioner}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  Ingen systeminformation angiven ännu.
                </Typography>
              )}
              <Box>
                <Button
                  size="small" variant="outlined" color="secondary"
                  startIcon={<EditIcon />}
                  onClick={() => { setEditingConfig(true); setConfigForm({ ...EMPTY_CONFIG, ...sysConfig }); }}
                >
                  {sysConfig ? 'Redigera systeminformation' : 'Lägg till systeminformation'}
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  );

  // ── Personal credentials panel ─────────────────────────────────────────────
  const credsPanel = (
    <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', backgroundColor: paperBg }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
        <VisibilityOffIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          Mina inloggningsuppgifter
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Endast synliga för dig
        </Typography>
      </Stack>
      <Divider />
      <Box sx={{ p: 2 }}>
        {editingCreds ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField size="small" label="Användarnamn" fullWidth
                value={credForm.anvandarnamn}
                onChange={e => setCredForm(f => ({ ...f, anvandarnamn: e.target.value }))}
              />
              <TextField
                size="small" label="Lösenord" fullWidth
                type={showPassword ? 'text' : 'password'}
                value={credForm.losenord}
                onChange={e => setCredForm(f => ({ ...f, losenord: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => { setEditingCreds(false); setCredForm({ ...EMPTY_CREDS, ...creds }); }}>
                Avbryt
              </Button>
              <Button size="small" variant="contained" startIcon={<SaveIcon />}
                onClick={handleSaveCreds} disabled={savingCreds}>
                {savingCreds ? 'Sparar…' : 'Spara'}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={3}>
              {[
                { label: 'Användarnamn', value: creds.anvandarnamn, copy: true },
                { label: 'Lösenord',     value: creds.losenord,     copy: true, secret: true },
              ].map(field => field.value ? (
                <Box key={field.label}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {field.label}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace' }}>
                      {field.secret ? '••••••••' : field.value}
                    </Typography>
                    <Tooltip title={lastCopied === field.label ? 'Kopierat!' : `Kopiera ${field.label.toLowerCase()}`}>
                      <IconButton size="small"
                        onClick={() => copyText(field.value, field.label)}
                        sx={{ color: lastCopied === field.label ? 'success.main' : 'text.disabled' }}>
                        {lastCopied === field.label
                          ? <CheckIcon sx={{ fontSize: 14 }} />
                          : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ) : null)}
              {!creds.anvandarnamn && !creds.losenord && (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  Inga uppgifter sparade ännu.
                </Typography>
              )}
            </Stack>
            <Box>
              <Button size="small" variant="outlined" color="secondary" startIcon={<EditIcon />}
                onClick={() => { setEditingCreds(true); setCredForm({ ...EMPTY_CREDS, ...creds }); }}>
                {creds.anvandarnamn ? 'Ändra uppgifter' : 'Lägg till mina uppgifter'}
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    </Paper>
  );

  if (!report) {
    return (
      <Box sx={{ maxWidth:760, mx:'auto' }}>
        {uploading && <LinearProgress sx={{ mb:2 }} />}
        {configPanel}
        {credsPanel}
        {uploadZone}
      </Box>
    );
  }

  // ── Render: data loaded ────────────────────────────────────────────────────
  const uploadedDate = report.uploadedAt?.toDate
    ? report.uploadedAt.toDate().toLocaleDateString('sv-SE')
    : '—';

  return (
    <Box sx={{ maxWidth:760, mx:'auto' }}>

      {uploading && <LinearProgress sx={{ mb:2 }} />}

      {configPanel}
      {credsPanel}

      {/* Report meta + re-upload + delete */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2} flexWrap="wrap">
        <Chip icon={<UploadFileIcon/>} label={report.fileName}
          size="small" variant="outlined" color="secondary" />
        <Typography variant="caption" sx={{ color:'text.secondary' }}>
          Uppladdad {uploadedDate} av {report.uploaderName}
        </Typography>
        <Box flex={1} />
        <Button
          component="label" htmlFor="et-tab-file"
          size="small" variant="outlined" startIcon={<CloudUploadIcon/>}
        >
          Byt fil
          <input
            id="et-tab-file" type="file" accept=".csv,.xlsx,.xls,.txt"
            style={{ display:'none' }}
            onChange={e => handleUploadFile(e.target.files[0])}
          />
        </Button>
        <Tooltip title="Radera uppladdad tiddata">
          <IconButton size="small" color="error" onClick={() => setConfirmDel(true)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDel} onClose={() => setConfirmDel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Radera tiddata?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            All uppladdad tiddata för detta uppdrag raderas. Teamets framstegsmarkering behålls.
            Åtgärden kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(false)}>Avbryt</Button>
          <Button
            color="error" variant="contained"
            onClick={handleDelete} disabled={deleting}
          >
            {deleting ? 'Raderar…' : 'Radera'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" mb={3}>
        <FormControl size="small" sx={{ minWidth:200 }}>
          <InputLabel>Person</InputLabel>
          <Select value={person} label="Person" onChange={e=>setPerson(e.target.value)}>
            {persons.map(p=><MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth:170 }}>
          <InputLabel>Månad</InputLabel>
          <Select value={month} label="Månad" onChange={e=>handleMonthChange(e.target.value)}>
            {months.map(m => {
              const [y,mo]=m.split('-');
              return <MenuItem key={m} value={m}>{SV_MONTHS[+mo-1]} {y}</MenuItem>;
            })}
          </Select>
        </FormControl>
        {totalH > 0 && (
          <Chip label={`${fmtH(totalH)} tim totalt`} size="small" variant="outlined" />
        )}
      </Stack>

      {/* Week navigator */}
      {weekStart && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={2} flexWrap="wrap">
            <IconButton size="small" onClick={prevWeek}><ChevronLeftIcon/></IconButton>
            <Typography variant="subtitle1" fontWeight={700}
              sx={{ minWidth:140, textAlign:'center' }}>
              v.{wkNum} · {monthLabel}
            </Typography>
            <IconButton size="small" onClick={nextWeek}><ChevronRightIcon/></IconButton>

            {daysWithEntries.length > 0 && (
              <Chip
                size="small"
                icon={allCopied ? <CheckCircleIcon sx={{ fontSize:16 }}/> : undefined}
                label={`${copiedCount} / ${daysWithEntries.length} dagar klara`}
                color={allCopied ? 'success' : 'default'}
                variant={allCopied ? 'filled' : 'outlined'}
              />
            )}
            <Tooltip title={weekFullyMarked ? 'Avmarkera vecka' : 'Markera hela veckan som klar'}>
              <Chip
                size="small"
                icon={weekFullyMarked
                  ? <CheckCircleIcon sx={{ fontSize:16 }}/>
                  : <RadioButtonUncheckedIcon sx={{ fontSize:16 }}/>}
                label={weekFullyMarked ? 'Vecka klar' : 'Markera vecka'}
                color={weekFullyMarked ? 'success' : 'default'}
                variant={weekFullyMarked ? 'filled' : 'outlined'}
                onClick={weekFullyMarked ? unmarkWeek : markWeekDone}
                sx={{ cursor:'pointer' }}
              />
            </Tooltip>
          </Stack>

          {/* Two-column: calendar + day cards */}
          <Stack direction={{ xs:'column', md:'row' }} spacing={3} alignItems="flex-start">

          {/* Left: calendar */}
          <Box sx={{ width:{ xs:'100%', md:200 }, flexShrink:0 }}>
            <Typography variant="caption" fontWeight={600}
              sx={{ color:'text.secondary', display:'block', mb:1 }}>
              {month ? (()=>{ const [y,m]=month.split('-'); return `${SV_MONTHS[+m-1]} ${y}`; })() : ''}
            </Typography>
            <MonthCalendar
              month={month} person={person} byDate={byDate} copied={copied}
              accentColor={accentColor}
              onDayClick={(day) => setWeekStart(mondayOf(day))}
            />
          </Box>

          {/* Right: day cards */}
          <Box flex={1} sx={{ minWidth:0, width:'100%' }}>
          <Stack spacing={1.5}>
            {weekDays.map((day, i) => {
              const dateStr    = isoDate(day);
              const dayEntries = byDate[dateStr] || [];
              const isCopied   = !!copied[`${person}:${dateStr}`];
              const dayTotalH  = dayEntries.reduce((s,r)=>s+r.antal, 0);
              const isEmpty    = dayEntries.length === 0;

              if (isEmpty) {
                return (
                  <Paper key={dateStr} variant="outlined"
                    sx={{ px:2, py:1, backgroundColor: paperBg }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ display:'flex', alignItems:'center', gap:1, minWidth:72 }}>
                        <Typography variant="caption"
                          sx={{ color:'text.disabled', fontWeight:600 }}>
                          {SV_DAYS[i]}
                        </Typography>
                        <Typography variant="body2"
                          sx={{ color:'text.disabled', fontWeight:700 }}>
                          {day.getDate()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color:'text.disabled' }}>—</Typography>
                    </Stack>
                  </Paper>
                );
              }

              return (
                <Paper key={dateStr} variant="outlined"
                  sx={{
                    p:1.5,
                    backgroundColor: isCopied ? successBg : paperBg,
                    borderColor: isCopied ? 'success.main' : undefined,
                    transition:'background-color 0.15s, border-color 0.15s',
                  }}>
                  <Stack direction="row" alignItems="flex-start" spacing={2}>

                    <Box sx={{ minWidth:64, textAlign:'center', pt:0.5 }}>
                      <Typography variant="caption"
                        sx={{ color:'text.secondary', fontWeight:600, display:'block' }}>
                        {SV_DAYS[i]}
                      </Typography>
                      <Typography variant="h5"
                        sx={{ fontWeight:700, lineHeight:1.1, color:'text.primary' }}>
                        {day.getDate()}
                      </Typography>
                      <Chip size="small" label={`${fmtH(dayTotalH)}h`}
                        color={isCopied ? 'success' : 'secondary'} variant="filled"
                        sx={{ mt:0.5, height:20, fontSize:'0.72rem' }} />
                    </Box>

                    <Divider orientation="vertical" flexItem />

                    <Box flex={1} sx={{ py:0.25, minWidth:0 }}>
                      <Stack spacing={1}>
                        {dayEntries.map((entry, j) => {
                          const hKey = `${dateStr}-${j}-h`;
                          const nKey = `${dateStr}-${j}-n`;
                          return (
                            <Box key={j}>
                              {j > 0 && <Divider sx={{ mb:1 }}/>}
                              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                                <Tooltip title={lastCopied===hKey ? 'Kopierat!' : 'Kopiera timmar'}>
                                  <Chip size="small"
                                    label={fmtH(entry.antal)}
                                    onClick={() => copyText(fmtHCopy(entry.antal), hKey)}
                                    icon={lastCopied===hKey
                                      ? <CheckIcon sx={{ fontSize:'14px !important' }}/>
                                      : <ContentCopyIcon sx={{ fontSize:'14px !important' }}/>}
                                    sx={{
                                      cursor:'pointer', flexShrink:0, fontWeight:700,
                                      backgroundColor: alpha(accentColor, isDark ? 0.25 : 0.12),
                                      color: isDark ? '#fff' : accentColor,
                                      '& .MuiChip-icon': { color:'inherit' },
                                    }}
                                  />
                                </Tooltip>
                                <Typography variant="body2"
                                  sx={{
                                    flex:1, lineHeight:1.4, wordBreak:'break-word', mx:0.5,
                                    color: entry.notering ? 'text.primary' : 'text.disabled',
                                  }}>
                                  {entry.notering || <em>Ingen kommentar</em>}
                                </Typography>
                                {entry.notering && (
                                  <Tooltip title={lastCopied===nKey ? 'Kopierat!' : 'Kopiera text'}>
                                    <IconButton size="small"
                                      onClick={() => copyText(entry.notering, nKey)}
                                      sx={{
                                        color: lastCopied===nKey ? 'success.main' : 'text.disabled',
                                        flexShrink:0,
                                      }}>
                                      {lastCopied===nKey
                                        ? <CheckIcon sx={{ fontSize:16 }}/>
                                        : <ContentCopyIcon sx={{ fontSize:16 }}/>}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          );
                        })}
                        {dayEntries.length > 1 && (
                          <Typography variant="caption" sx={{ color:'text.secondary' }}>
                            {dayEntries.length} rader · {fmtH(dayTotalH)}h totalt
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    <Tooltip title={isCopied ? 'Avmarkera dag' : 'Markera dag som klar'}>
                      <IconButton size="small" onClick={() => toggleDay(dateStr)}
                        color={isCopied ? 'success' : 'default'}
                        sx={{ mt:0.5, flexShrink:0 }}>
                        {isCopied ? <CheckCircleIcon/> : <RadioButtonUncheckedIcon/>}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          {daysWithEntries.length === 0 && (
            <Alert severity="info" sx={{ mt:2 }}>
              Ingen tid registrerad för {person} denna vecka.
            </Alert>
          )}

          {(allCopied || weekFullyMarked) && (
            <Box mt={2.5} textAlign="center">
              <Button variant="contained" color="success" onClick={nextWeek}
                endIcon={<ChevronRightIcon/>} sx={{ borderRadius:5, px:3 }}>
                Nästa vecka
              </Button>
            </Box>
          )}
          </Box>{/* end right column */}
          </Stack>{/* end two-column */}
        </>
      )}
    </Box>
  );
}
